import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../contexts/AuthProvider';
import workflowService from './workflowService';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { justificativaCalendarioFiscal } from './justificativaSox';

type Bloqueio = { etapa: number; titulo: string; motivo: string };

type OnboardingStatus = {
  etapa1Concluida: boolean; etapa2Concluida: boolean;
  etapa3Concluida: boolean; etapa4Concluida: boolean; etapa5Concluida: boolean;
  goLiveAtivo: boolean; goLiveEm: string | null;
  etapasConcluidas: number; progressoPercent: number;
  bloqueios: Bloqueio[];
};

const DEFAULT_STATUS: OnboardingStatus = {
  etapa1Concluida: false, etapa2Concluida: false,
  etapa3Concluida: false, etapa4Concluida: false, etapa5Concluida: false,
  goLiveAtivo: false, goLiveEm: null, etapasConcluidas: 0, progressoPercent: 0, bloqueios: [],
};

// Justificativas SOX padrão por etapa (seção 11) — pré-preenchidas e editáveis
// ao concluir cada etapa do onboarding.
export const JUSTIFICATIVAS_SOX_PADRAO: Record<number, string> = {
  1: 'Criação dos grupos econômicos, cadastro e gerenciamento das empresas, classificação das contas contábeis e mapeamento De/Para concluídos e validados, com as empresas vinculadas aos respectivos grupos econômicos e as contas associadas aos grupos e subgrupos correspondentes.',
  2: 'Alçadas de aprovação configuradas e validadas conforme os níveis hierárquicos e cargos definidos, estabelecendo os limites de autoridade aplicáveis a cada nível e respectivo escopo.',
  3: 'Matriz de aprovadores configurada e validada conforme a estrutura organizacional definida, com identificação dos responsáveis titulares e substitutos por nível e escopo de atuação.',
  4: 'Prazos de SLA em dias úteis, regras de escalonamento e travas de governança configurados e validados, garantindo controle dos prazos, segregação de funções e tratamento das situações de exceção.',
  5: 'Todas as configurações obrigatórias do processo foram revisadas e validadas. Hierarquia, matriz de aprovadores, alçadas, SLAs e controles de governança encontram-se aptos para utilização, autorizando a ativação do monitoramento e início da operação.',
};

const ETAPAS = [
  { num: 1, titulo: 'Hierarquia e De/Para',      icone: '🏢', desc: 'Grupos Econômicos → Empresas → Mapeamento De/Para de contas.' },
  { num: 2, titulo: 'Cargos, Usuários e Alçadas', icone: '⚖️', desc: 'Estruture os níveis N1 a N7, vincule os usuários e defina os limites de valor.' },
  { num: 3, titulo: 'Matriz de Aprovadores',      icone: '👥', desc: 'Vínculo: Usuário Aprovador × Grupo de Contas.' },
  { num: 4, titulo: 'SLA e Travas de Governança', icone: '⏱️', desc: 'Dias úteis (BrasilAPI) e Hard Stops.' },
  { num: 5, titulo: 'Validação e Go-Live',        icone: '🚀', desc: 'Checagem final e ativação do monitoramento.' },
];

// Etapa 4 tem 2 sub-passos próprios (EtapaConteudo4), não entra no PASSOS[]
const PASSOS_TOTAL: Record<number, number> = { 1: 3, 2: 4, 3: 1, 4: 2 };

// Sub-passos navegáveis por etapa (etapas 1-3 têm links externos; etapa 4 é inline)
// Etapa 2: primeiro os Cargos são estruturados, depois os Usuários são vinculados a eles,
// e por fim a Matriz de Acessos — sem ela, o usuário fica cadastrado mas não enxerga
// nenhuma empresa (é uma camada de permissão separada do cargo/nível de aprovação).
const PASSOS: Record<number, { titulo: string; desc: string; link: string; label: string }[]> = {
  1: [
    { titulo: 'Cadastro de Grupos Econômicos',        desc: 'Crie grupos e vincule as empresas importadas.',                                         link: '/workflow-aprovacao/cadastro-grupos',          label: 'Cadastrar Grupos' },
    { titulo: 'Empresas',                             desc: 'Visualize e edite os vínculos de empresa × grupo.',                                     link: '/workflow-aprovacao/grupos-economicos',        label: 'Gerenciar' },
    { titulo: 'De/Para — Hierarquia de Grupos de Contas', desc: 'Mapeie as contas ECD para grupos e subgrupos do plano de negócio (obrigatório SOX).', link: '/workflow-aprovacao/cadastro-depara',         label: 'Configurar De/Para' },
  ],
  2: [
    { titulo: 'Cadastro de Cargos (N1–N7)',           desc: 'Crie os níveis hierárquicos com descrição e permissões.',                              link: '/workflow-aprovacao/cadastro-cargos',          label: 'Cadastrar Cargos' },
    { titulo: 'Cadastro de Usuários do Workflow SOX', desc: 'Vincule os usuários do sistema aos cargos cadastrados acima.',                         link: '/workflow-aprovacao/cadastro-usuarios',        label: 'Cadastrar Usuários' },
    { titulo: 'Matriz de Acessos (Usuário × Empresa)', desc: 'Vincule cada usuário às empresas que ele pode acessar — sem isso, ninguém enxerga nenhuma conciliação.', link: '/workflow-aprovacao/matriz-acessos', label: 'Configurar Acessos' },
    { titulo: 'Configuração de Alçadas',              desc: 'Defina valor mínimo, máximo e SLA (dias úteis) por nível.',                            link: '/workflow-aprovacao/config-alcadas',           label: 'Configurar Alçadas' },
  ],
  3: [
    { titulo: 'Matriz de Responsabilidades SOX',      desc: 'Vincule aprovadores às contas contábeis. Validação SoD automática.',                    link: '/workflow-aprovacao/matriz-responsabilidades', label: 'Configurar Matriz' },
  ],
};

// ─── Componente de lista sequencial de sub-passos ────────────────────────────

const SubPassoLista: React.FC<{
  etapa: number;
  subPassoAtivo: number;
  onAvancar: () => void;
  history: any;
}> = ({ etapa, subPassoAtivo, onAvancar, history }) => {
  const passos = PASSOS[etapa] ?? [];
  const todosConcluidos = subPassoAtivo >= passos.length;

  return (
    <div>
      {passos.map((passo, idx) => {
        const concluido = idx < subPassoAtivo;
        const ativo     = idx === subPassoAtivo;
        const bloqueado = idx > subPassoAtivo;

        const borderColor = concluido ? '#50cd89' : ativo ? '#009ef7' : '#e9ecef';
        const bg          = concluido ? '#f0fdf8' : ativo ? '#f0f9ff' : '#fafafa';

        return (
          <div key={idx} style={{
            border: `1.5px solid ${borderColor}`, borderRadius: 10, padding: '16px 20px',
            marginBottom: 12, background: bg, opacity: bloqueado ? 0.45 : 1, transition: 'all .2s',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: concluido ? '#50cd89' : ativo ? '#009ef7' : '#e9ecef',
                  color: concluido || ativo ? '#fff' : '#a1a5b7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 14,
                }}>
                  {concluido ? '✓' : bloqueado ? '🔒' : idx + 1}
                </div>
                <div>
                  <strong style={{ color: bloqueado ? '#a1a5b7' : '#181c32', fontSize: 14 }}>{passo.titulo}</strong>
                  <p style={{ margin: '2px 0 0', fontSize: 12, color: bloqueado ? '#c4c4c4' : '#7e8299' }}>{passo.desc}</p>
                </div>
              </div>

              {!bloqueado && (
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    className='wf-btn wf-btn--secondary wf-btn--sm'
                    onClick={() => history.push(passo.link, { fromOnboarding: true, etapa })}
                  >
                    {passo.label}
                  </button>
                  {ativo && (
                    <button
                      className='wf-btn wf-btn--primary wf-btn--sm'
                      onClick={onAvancar}
                      style={{ background: '#50cd89', borderColor: '#50cd89' }}
                    >
                      Concluído
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {todosConcluidos && (
        <div style={{
          background: '#e8fff3', border: '1px solid #50cd89', borderRadius: 10,
          padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10, marginTop: 4,
        }}>
          <span style={{ fontSize: 18, color: '#50cd89' }}>✅</span>
          <strong style={{ color: '#1a9954', fontSize: 14 }}>
            Todos os passos concluídos! Clique em "Marcar Etapa como Concluída" abaixo.
          </strong>
        </div>
      )}
    </div>
  );
};

// ─── Wizard principal ────────────────────────────────────────────────────────

const OnboardingWizardPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ prefetchedStatus?: OnboardingStatus }>();
  const { usuario } = useAuth();
  const tenantId = usuario?.clienteID ?? 1;

  const [etapaAtiva, setEtapaAtiva] = useState(1);
  const [status, setStatus] = useState<OnboardingStatus>(DEFAULT_STATUS);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  // Estado dos sub-passos por etapa — persistido em localStorage
  const [subPassos, setSubPassos] = useState<Record<number, number>>(() => {
    try {
      const s = localStorage.getItem(`wf-subpassos-${tenantId}`);
      return s ? JSON.parse(s) : { 1: 0, 2: 0, 3: 0, 4: 0 };
    } catch {
      return { 1: 0, 2: 0, 3: 0, 4: 0 };
    }
  });

  // Etapa 4 — calendário
  const [anoSync, setAnoSync] = useState(new Date().getFullYear());
  // Justificativa SOX da sincronização já sugerida (cita o ano); a pessoa edita se quiser.
  const justSync = useJustificativaSugerida(justificativaCalendarioFiscal(anoSync));
  const [sincronizando, setSincronizando] = useState(false);
  const [feriados, setFeriados] = useState<{ data: string; descricao: string }[]>([]);
  const [calendarioSincronizado, setCalendarioSincronizado] = useState(false);

  // Etapa 4 — calculadora SLA
  const [calcInicio, setCalcInicio] = useState('');
  const [calcDias, setCalcDias] = useState('5');
  const [calcResult, setCalcResult] = useState<{ dataFim: string; feriadosExcluidos: number; finsDeSemanaExcluidos: number } | null>(null);
  const [calculando, setCalculando] = useState(false);

  const isConcluida = (n: number) => {
    switch (n) {
      case 1: return status.etapa1Concluida;
      case 2: return status.etapa2Concluida;
      case 3: return status.etapa3Concluida;
      case 4: return status.etapa4Concluida;
      case 5: return status.etapa5Concluida;
      default: return false;
    }
  };

  // Etapa N está disponível se todas as anteriores estiverem concluídas
  const podeIrParaEtapa = (n: number): boolean => {
    for (let i = 1; i < n; i++) {
      if (!isConcluida(i)) return false;
    }
    return true;
  };

  const avancarSubPasso = (etapa: number) => {
    const totalPassos = PASSOS_TOTAL[etapa] ?? 0;
    const atual = subPassos[etapa] ?? 0;
    if (atual >= totalPassos) return;
    const novos = { ...subPassos, [etapa]: atual + 1 };
    setSubPassos(novos);
    try { localStorage.setItem(`wf-subpassos-${tenantId}`, JSON.stringify(novos)); } catch {}
  };

  const carregar = async (statusInicial?: OnboardingStatus, goToEtapa?: number) => {
    try {
      setCarregando(true);
      const s = statusInicial ?? await workflowService.onboardingStatus(tenantId);
      setStatus(s as OnboardingStatus);

      const etapas = [s.etapa1Concluida, s.etapa2Concluida, s.etapa3Concluida, s.etapa4Concluida, s.etapa5Concluida];
      const primeiraIncompleta = etapas.findIndex(e => !e);
      const computed = primeiraIncompleta === -1 ? 5 : primeiraIncompleta + 1;
      setEtapaAtiva(goToEtapa ?? computed);
    } catch {
      // wizard continua funcionando sem backend
    } finally {
      setCarregando(false);
    }
    workflowService.listarFeriados(anoSync)
      .then(cal => {
        const lista = cal.feriados ?? [];
        setFeriados(lista);
        if (lista.length > 0) setCalendarioSincronizado(true);
      })
      .catch(() => {});
  };

  useEffect(() => {
    const goToEtapa = (location.state as any)?.goToEtapa as number | undefined;
    carregar(location.state?.prefetchedStatus, goToEtapa);
  }, []); // eslint-disable-line

  const bloqueio = (n: number) => (status.bloqueios ?? []).find(b => b.etapa === n);

  const concluirEtapa = async (etapa: number) => {
    const { value: justificativa } = await Swal.fire({
      title: `Concluir Etapa ${etapa}`,
      input: 'textarea',
      inputLabel: 'Justificativa SOX (obrigatório — pré-preenchida, edite se necessário)',
      inputValue: JUSTIFICATIVAS_SOX_PADRAO[etapa] ?? '',
      inputPlaceholder: 'Descreva o que foi configurado e quem realizou a ação...',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      inputValidator: v => (!v?.trim() ? 'A justificativa SOX é obrigatória.' : null),
    });
    if (!justificativa) return;
    try {
      setSalvando(true);
      await workflowService.concluirEtapaOnboarding({ tenantId, etapa, justificativaSox: justificativa });
      await carregar();
      if (etapa === 5) {
        await Swal.fire({ icon: 'success', title: '🚀 Go-Live ativado com sucesso!', text: 'O Motor de Conciliação está ativo. Redirecionando para o dashboard...', timer: 2500, showConfirmButton: false });
        history.push('/workflow-aprovacao', { goLiveJustCompleted: true });
      } else {
        Swal.fire({ icon: 'success', title: `Etapa ${etapa} marcada como concluída!`, timer: 1800, showConfirmButton: false });
      }
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err?.response?.data?.message ?? 'Não foi possível salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const sincronizarFeriados = async () => {
    if (!justSync.texto.trim()) {
      Swal.fire({ icon: 'warning', title: 'Informe a justificativa SOX antes de sincronizar.' });
      return;
    }
    try {
      setSincronizando(true);
      const res = await workflowService.sincronizarCalendario(anoSync, justSync.texto.trim());
      setFeriados(res.feriados ?? []);
      setCalendarioSincronizado(true);
      if (res.jaSincronizado) {
        Swal.fire({ icon: 'info', title: res.mensagem ?? `Os feriados de ${anoSync} já estão sincronizados e disponíveis em cache.`, timer: 2500, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'success', title: `${res.feriadosImportados} feriados importados de ${anoSync}!`, timer: 2000, showConfirmButton: false });
      }
    } catch (err: any) {
      // O backend responde com corpo de texto puro em alguns erros (ex.: 403 "Usuário não
      // cadastrado no módulo Workflow") e com objeto { message, detalhe } em outros (ex.: 502
      // BrasilAPI indisponível) — sem checar o tipo, err.response.data.message ficava undefined
      // no primeiro caso e a mensagem real do problema nunca aparecia, só o fallback genérico.
      const data = err?.response?.data;
      const mensagem = typeof data === 'string' ? data : (data?.message ?? 'BrasilAPI indisponível.');
      Swal.fire({ icon: 'error', title: 'Erro ao sincronizar', text: mensagem });
    } finally {
      setSincronizando(false);
    }
  };

  const calcularSla = async () => {
    if (!calcInicio) { Swal.fire({ icon: 'warning', title: 'Informe a data de início.' }); return; }
    const dias = parseInt(calcDias);
    if (!dias || dias < 1) { Swal.fire({ icon: 'warning', title: 'Informe um número de dias válido.' }); return; }
    try {
      setCalculando(true);
      const res = await workflowService.calcularSla(calcInicio, dias);
      setCalcResult(res);
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro ao calcular. Sincronize o calendário fiscal primeiro.' });
    } finally {
      setCalculando(false);
    }
  };

  if (carregando) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 12, color: '#a1a5b7' }}>
      <div style={{ width: 28, height: 28, border: '3px solid #e8f4fb', borderTopColor: '#005e96', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <span>Carregando wizard...</span>
    </div>
  );

  return (
    <div className='wf-page'>
      {/* Header */}
      <div className='wf-header'>
        <div>
          <h1 className='wf-header__title'>Onboarding de Governança SOX</h1>
          <p className='wf-header__subtitle'>Wizard de configuração obrigatória — 5 etapas para ativar o motor de aprovação.</p>
        </div>
        <button className='wf-btn wf-btn--secondary' onClick={() => history.push('/workflow-aprovacao')}>Voltar ao dashboard</button>
      </div>

      {/* Barra de progresso */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '24px 32px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 12 }}>
          {ETAPAS.map((e, i) => {
            const concluida  = isConcluida(e.num);
            const ativa      = etapaAtiva === e.num;
            const disponivel = podeIrParaEtapa(e.num);
            const cor        = concluida ? '#50cd89' : ativa ? '#009ef7' : disponivel ? '#e9ecef' : '#f1f1f2';
            const textCor    = concluida || ativa ? '#fff' : '#7e8299';

            return (
              <React.Fragment key={e.num}>
                <div
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, cursor: disponivel ? 'pointer' : 'not-allowed' }}
                  onClick={() => { if (disponivel) setEtapaAtiva(e.num); }}
                  title={disponivel ? undefined : 'Complete as etapas anteriores primeiro'}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', background: cor, color: textCor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                    fontSize: 14, border: ativa ? '2px solid #009ef7' : '2px solid ' + cor,
                    boxShadow: ativa ? '0 0 0 4px rgba(0,158,247,0.15)' : 'none', transition: 'all .2s',
                    opacity: disponivel ? 1 : 0.4,
                  }}>
                    {concluida ? '✓' : disponivel ? e.num : '🔒'}
                  </div>
                  <div style={{ fontSize: 11, marginTop: 6, color: ativa ? '#009ef7' : concluida ? '#50cd89' : '#a1a5b7', fontWeight: ativa ? 700 : 500, textAlign: 'center', maxWidth: 90, opacity: disponivel ? 1 : 0.4 }}>
                    {e.titulo}
                  </div>
                </div>
                {i < ETAPAS.length - 1 && (
                  <div style={{ height: 2, flex: 1, background: isConcluida(e.num) ? '#50cd89' : '#e9ecef', marginBottom: 22, borderRadius: 2 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div style={{ height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${status.progressoPercent}%`, background: 'linear-gradient(90deg,#009ef7,#50cd89)', borderRadius: 3, transition: 'width .5s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: '#7e8299' }}>
          <span>{status.etapasConcluidas} de 5 etapas concluídas</span>
          <span>{status.progressoPercent}% completo {status.goLiveAtivo ? '— ✅ Go-Live ativo' : ''}</span>
        </div>
      </div>

      {/* Conteúdo da etapa ativa */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '32px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        {(() => {
          const etapa       = ETAPAS[etapaAtiva - 1];
          const concluida   = isConcluida(etapa.num);
          const bloqueioDaEtapa = bloqueio(etapa.num);
          const subPassoAtivo   = subPassos[etapa.num] ?? 0;
          const passosEtapa     = PASSOS[etapa.num] ?? [];
          const todosPassosDone = subPassoAtivo >= (PASSOS_TOTAL[etapa.num] ?? passosEtapa.length);

          return (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ fontSize: 40 }}>{etapa.icone}</div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 20, color: '#181c32' }}>Etapa {etapa.num} — {etapa.titulo}</h2>
                    <p style={{ margin: 0, color: '#7e8299', fontSize: 14, marginTop: 4 }}>{etapa.desc}</p>
                  </div>
                </div>
                {concluida && (
                  <span style={{ background: '#e8fff3', color: '#50cd89', padding: '6px 16px', borderRadius: 20, fontWeight: 700, fontSize: 13 }}>
                    ✓ Concluída
                  </span>
                )}
              </div>

              {bloqueioDaEtapa && !concluida && (
                <div style={{ background: '#fff8dd', border: '1px solid #ffc700', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>⚠️</span>
                  <div>
                    <strong style={{ color: '#a1800c' }}>{bloqueioDaEtapa.titulo}</strong>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#7e8299' }}>{bloqueioDaEtapa.motivo}</p>
                  </div>
                </div>
              )}

              {/* Conteúdo específico por etapa */}
              {etapa.num <= 3 && (
                <SubPassoLista
                  etapa={etapa.num}
                  subPassoAtivo={subPassoAtivo}
                  onAvancar={() => avancarSubPasso(etapa.num)}
                  history={history}
                />
              )}
              {etapa.num === 4 && (
                <EtapaConteudo4
                  history={history}
                  subPassoAtivo={subPassoAtivo}
                  onAvancar={() => avancarSubPasso(4)}
                  anoSync={anoSync} setAnoSync={setAnoSync}
                  justSync={justSync.texto} setJustSync={justSync.editar}
                  sincronizando={sincronizando} onSincronizar={sincronizarFeriados}
                  feriados={feriados}
                  calendarioSincronizado={calendarioSincronizado}
                  calcInicio={calcInicio} setCalcInicio={setCalcInicio}
                  calcDias={calcDias} setCalcDias={setCalcDias}
                  calculando={calculando} calcResult={calcResult} onCalcular={calcularSla}
                />
              )}
              {etapa.num === 5 && (
                <EtapaConteudo5 status={status} onGoLive={() => concluirEtapa(5)} salvando={salvando} />
              )}

              {/* Botões de navegação */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, paddingTop: 20, borderTop: '1px solid #f1f1f2' }}>
                <button
                  className='wf-btn wf-btn--secondary'
                  disabled={etapaAtiva === 1}
                  onClick={() => setEtapaAtiva(e => Math.max(1, e - 1))}
                >
                  ← Anterior
                </button>

                <div style={{ display: 'flex', gap: 12 }}>
                  {!concluida && etapa.num < 5 && todosPassosDone && (
                    <button
                      className='wf-btn wf-btn--primary'
                      disabled={salvando}
                      onClick={() => concluirEtapa(etapa.num)}
                    >
                      {salvando ? 'Salvando...' : `Marcar Etapa ${etapa.num} como Concluída`}
                    </button>
                  )}
                  {etapaAtiva < 5 && podeIrParaEtapa(etapaAtiva + 1) && (
                    <button
                      className='wf-btn wf-btn--secondary'
                      onClick={() => setEtapaAtiva(e => Math.min(5, e + 1))}
                    >
                      Próxima →
                    </button>
                  )}
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

// ─── Etapa 4 — sequencial com 2 sub-passos ───────────────────────────────────

const EtapaConteudo4: React.FC<{
  history: any;
  subPassoAtivo: number; onAvancar: () => void;
  anoSync: number; setAnoSync: (v: number) => void;
  justSync: string; setJustSync: (v: string) => void;
  sincronizando: boolean; onSincronizar: () => void;
  feriados: { data: string; descricao: string }[];
  calendarioSincronizado: boolean;
  calcInicio: string; setCalcInicio: (v: string) => void;
  calcDias: string; setCalcDias: (v: string) => void;
  calculando: boolean; calcResult: any; onCalcular: () => void;
}> = ({ history, subPassoAtivo, onAvancar, anoSync, setAnoSync, justSync, setJustSync, sincronizando, onSincronizar, feriados, calendarioSincronizado, calcInicio, setCalcInicio, calcDias, setCalcDias, calculando, calcResult, onCalcular }) => {

  const PASSOS_4 = [
    { titulo: 'Sincronizar Calendário Fiscal',    desc: 'Importe os feriados nacionais via BrasilAPI para cálculo de dias úteis.' },
    { titulo: 'Configurar SLA e Calculadora',     desc: 'Defina prazos por etapa e valide com a calculadora de dias úteis.' },
  ];

  const bloqueado0 = subPassoAtivo < 0; // nunca bloqueado (sempre visível)
  const bloqueado1 = subPassoAtivo < 1;

  const stepCard = (idx: number, titulo: string, desc: string, children: React.ReactNode) => {
    const concluido = idx < subPassoAtivo;
    const ativo     = idx === subPassoAtivo;
    const bloqueado = idx > subPassoAtivo;

    return (
      <div style={{
        border: `1.5px solid ${concluido ? '#50cd89' : ativo ? '#009ef7' : '#e9ecef'}`,
        borderRadius: 10, marginBottom: 20,
        background: concluido ? '#f0fdf8' : ativo ? '#f0f9ff' : '#fafafa',
        opacity: bloqueado ? 0.45 : 1, overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: (ativo && !concluido) ? '1px solid #e9ecef' : 'none' }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: concluido ? '#50cd89' : ativo ? '#009ef7' : '#e9ecef',
            color: concluido || ativo ? '#fff' : '#a1a5b7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14,
          }}>
            {concluido ? '✓' : bloqueado ? '🔒' : idx + 1}
          </div>
          <div style={{ flex: 1 }}>
            <strong style={{ color: bloqueado ? '#a1a5b7' : '#181c32', fontSize: 14 }}>{titulo}</strong>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: bloqueado ? '#c4c4c4' : '#7e8299' }}>{desc}</p>
          </div>
          {concluido && <span style={{ fontSize: 13, color: '#50cd89', fontWeight: 600 }}>✓ Concluído</span>}
        </div>
        {ativo && <div style={{ padding: '16px 20px' }}>{children}</div>}
      </div>
    );
  };

  return (
    <div>
      <p style={{ color: '#7e8299', fontSize: 14, marginBottom: 20 }}>
        Sincronize o calendário de feriados nacionais e configure os SLAs de aprovação. Siga a ordem abaixo.
      </p>

      {/* Sub-passo 0: Calendário */}
      {stepCard(0, PASSOS_4[0].titulo, PASSOS_4[0].desc, (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              Ano
              <input type='number' value={anoSync} onChange={e => setAnoSync(Number(e.target.value))}
                style={{ width: 90, padding: '6px 10px', border: '1px solid #e9ecef', borderRadius: 6, fontSize: 14 }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: 1 }}>
              Justificativa SOX (obrigatória)
              <input value={justSync} onChange={e => setJustSync(e.target.value)}
                placeholder='Ex: Sincronização anual de feriados nacionais para cálculo de SLA.'
                style={{ padding: '6px 10px', border: '1px solid #e9ecef', borderRadius: 6, fontSize: 14 }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={onSincronizar} disabled={sincronizando}>
              {sincronizando ? 'Sincronizando...' : `↓ Sincronizar feriados de ${anoSync}`}
            </button>
            {calendarioSincronizado && (
              <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={onAvancar} style={{ background: '#50cd89', borderColor: '#50cd89', color: '#fff' }}>
                Concluído
              </button>
            )}
          </div>
          {!calendarioSincronizado && (
            <p style={{ fontSize: 12, color: '#f64e60', marginTop: 8 }}>⚠ Sincronize os feriados antes de avançar.</p>
          )}
          {feriados.length > 0 && (
            <div style={{ marginTop: 14, maxHeight: 180, overflowY: 'auto', border: '1px solid #f1f1f2', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 12, color: '#a1a5b7', marginBottom: 8 }}>{feriados.length} feriados em cache</div>
              {feriados.map((f, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', borderBottom: i < feriados.length - 1 ? '1px solid #f1f1f2' : 'none' }}>
                  <span style={{ color: '#181c32' }}>{f.descricao}</span>
                  <span style={{ color: '#7e8299', fontFamily: 'monospace' }}>{f.data}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ))}

      {/* Sub-passo 1: Calculadora + link config SLA */}
      {stepCard(1, PASSOS_4[1].titulo, PASSOS_4[1].desc, (
        <>
          {/* Calculadora SLA */}
          <div style={{ marginBottom: 16 }}>
            <strong style={{ fontSize: 13, color: '#181c32', display: 'block', marginBottom: 8 }}>Calculadora de SLA em Dias Úteis</strong>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                Data de início
                <input type='date' value={calcInicio} onChange={e => setCalcInicio(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #e9ecef', borderRadius: 6, fontSize: 14 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                Dias úteis
                <input type='number' min={1} max={365} value={calcDias} onChange={e => setCalcDias(e.target.value)}
                  style={{ width: 80, padding: '6px 10px', border: '1px solid #e9ecef', borderRadius: 6, fontSize: 14 }} />
              </label>
              <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={onCalcular} disabled={calculando}>
                {calculando ? 'Calculando...' : 'Calcular'}
              </button>
            </div>
            {calcResult && (
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
                  {[
                    { label: 'Data de vencimento', value: new Date(calcResult.dataFim).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }), destaque: true },
                    { label: 'Feriados excluídos', value: calcResult.feriadosExcluidos },
                    { label: 'Fins de semana excluídos', value: calcResult.finsDeSemanaExcluidos },
                  ].map(({ label, value, destaque }) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: destaque ? 16 : 22, fontWeight: 700, color: destaque ? '#009ef7' : '#181c32' }}>{value}</div>
                      <div style={{ fontSize: 11, color: '#a1a5b7' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Link para config de alçadas (onde o SLA por etapa/nível é definido) */}
          <div style={{ border: '1px solid #e9ecef', borderRadius: 8, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, background: '#fff' }}>
            <div>
              <strong style={{ color: '#181c32', fontSize: 14 }}>Configuração de SLA por alçada</strong>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#7e8299' }}>Defina o SLA em dias úteis por nível e escopo na Etapa 2 — Config. de Alçadas.</p>
            </div>
            <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => history.push('/workflow-aprovacao/config-alcadas', { fromOnboarding: true, etapa: 2 })}>
              Configurar Alçadas →
            </button>
          </div>

          {calcResult
            ? (
              <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={onAvancar} style={{ background: '#50cd89', borderColor: '#50cd89' }}>
                Concluído
              </button>
            ) : (
              <p style={{ fontSize: 12, color: '#7e8299', marginTop: 4 }}>Use a calculadora acima para validar o SLA antes de avançar.</p>
            )
          }
        </>
      ))}

      {subPassoAtivo >= 2 && (
        <div style={{ background: '#e8fff3', border: '1px solid #50cd89', borderRadius: 10, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18, color: '#50cd89' }}>✅</span>
          <strong style={{ color: '#1a9954', fontSize: 14 }}>
            Todos os passos concluídos! Clique em "Marcar Etapa como Concluída" abaixo.
          </strong>
        </div>
      )}
    </div>
  );
};

// ─── Etapa 5 — Go-Live ────────────────────────────────────────────────────────

const EtapaConteudo5: React.FC<{ status: OnboardingStatus; onGoLive: () => void; salvando: boolean }> = ({ status, onGoLive, salvando }) => {
  const etapas = [
    { num: 1, label: 'Hierarquia e De/Para',         ok: status.etapa1Concluida },
    { num: 2, label: 'Cargos, Usuários e Alçadas (N1–N7)', ok: status.etapa2Concluida },
    { num: 3, label: 'Matriz de Aprovadores',         ok: status.etapa3Concluida },
    { num: 4, label: 'SLA e Travas de Governança',   ok: status.etapa4Concluida },
  ];
  const tudo = etapas.every(e => e.ok);

  return (
    <div>
      <p style={{ color: '#7e8299', fontSize: 14, marginBottom: 20 }}>
        Verifique se todas as etapas anteriores foram concluídas e ative o monitoramento do Kanban de conciliações.
      </p>
      <div style={{ marginBottom: 24 }}>
        {etapas.map(e => (
          <div key={e.num} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f1f1f2' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: e.ok ? '#e8fff3' : '#fff5f8', color: e.ok ? '#50cd89' : '#f1416c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
              {e.ok ? '✓' : '✗'}
            </div>
            <span style={{ fontSize: 14, color: e.ok ? '#181c32' : '#7e8299' }}>Etapa {e.num} — {e.label}</span>
          </div>
        ))}
      </div>

      {status.goLiveAtivo ? (
        <div style={{ background: '#e8fff3', border: '1px solid #50cd89', borderRadius: 10, padding: '20px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🚀</div>
          <h3 style={{ margin: 0, color: '#1a9954' }}>Go-Live ativado!</h3>
          <p style={{ margin: '8px 0 0', color: '#47be7d', fontSize: 13 }}>
            Ativado em {status.goLiveEm ? new Date(status.goLiveEm).toLocaleString('pt-BR') : '—'}
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#7e8299' }}>
            O Motor de Conciliação já está monitorando o Kanban e disparando o fluxo de aprovação SOX automaticamente.
          </p>
        </div>
      ) : tudo ? (
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <p style={{ color: '#50cd89', fontWeight: 600, marginBottom: 16 }}>✅ Todos os pré-requisitos validados. Pronto para Go-Live!</p>
          <button className='wf-btn wf-btn--primary' style={{ background: '#50cd89', borderColor: '#50cd89', fontSize: 15, padding: '14px 32px' }} disabled={salvando} onClick={onGoLive}>
            {salvando ? 'Ativando...' : '🚀 Ativar Go-Live'}
          </button>
        </div>
      ) : (
        <div style={{ background: '#fff5f8', border: '1px solid #f1416c', borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
          <strong style={{ color: '#f1416c' }}>Há etapas pendentes.</strong>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#7e8299' }}>Conclua todas as etapas anteriores antes de ativar o Go-Live.</p>
        </div>
      )}
    </div>
  );
};

export default OnboardingWizardPage;
